/**
 * StageTracker — the live "watch it work" surface for a YAGNI Code run.
 *
 * Renders the five pipeline stages (map -> plan -> implement -> review -> fix)
 * as a compact tracker and highlights the one the `/go` run is on right now.
 * On mount it backfills from GET /api/work-items/:id/coding-events (filtering
 * the `stage` coding-event kind), then subscribes to the live `coding_event`
 * window CustomEvent (the same window-event pattern useWorkItemActivity uses)
 * to advance the active stage as the run progresses.
 *
 * Newspaper-restrained motion: a single subtle spinner on the active stage,
 * never a wall of tool-call logs. Renders the whole rail (greyed) even before
 * any stage event arrives, so a freshly-started run reads as "lining up".
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import type { CodingEvent, CodingStageEvent } from "@yagni/shared";

const STAGES = ["map", "plan", "implement", "review", "fix"] as const;
type Stage = (typeof STAGES)[number];

function isStageStart(event: unknown): event is CodingStageEvent {
  if (!event || typeof event !== "object") return false;
  const e = event as { kind?: unknown; phase?: unknown; stage?: unknown };
  return (
    e.kind === "stage" &&
    e.phase === "start" &&
    typeof e.stage === "string" &&
    (STAGES as readonly string[]).includes(e.stage)
  );
}

export function StageTracker({
  workItemId,
  runId,
  round,
}: {
  workItemId: string;
  /**
   * The attempt to watch. Absent keeps the old whole-item read; given, the
   * backfill and the live stream are both scoped to this run, which is what
   * stops attempt 2's rail from showing attempt 1's stage.
   */
  runId?: string | null;
  round?: number;
}) {
  const [active, setActive] = useState<Stage | null>(null);

  // Backfill the latest started stage from the persisted event log.
  useEffect(() => {
    let cancelled = false;
    const query = runId ? `?runId=${encodeURIComponent(runId)}` : "";
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/coding-events${query}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ events?: CodingEvent[] }>) : null))
      .then((data) => {
        if (cancelled || !data) return;
        const starts = (data.events ?? []).filter(isStageStart);
        const last = starts[starts.length - 1];
        if (last) setActive(last.stage);
      })
      .catch(() => {
        // No backfill is fine — the live subscription will fill it in.
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, runId]);

  // Live updates over the coding-events WS, re-emitted as a window CustomEvent.
  // A frame that names a different run — or, while an attempt is being watched,
  // no run at all — cannot prove it belongs to the rail on screen.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{ workItemId?: string; runId?: string; event?: unknown }>
      ).detail;
      if (!detail || detail.workItemId !== workItemId) return;
      if (runId && detail.runId !== runId) return;
      if (isStageStart(detail.event)) setActive(detail.event.stage);
    };
    window.addEventListener("coding_event", handler);
    return () => window.removeEventListener("coding_event", handler);
  }, [workItemId, runId]);

  return (
    <div className="wk-stages" aria-label="Pipeline stage">
      <div className="is-art-lab">
        Session
        {typeof round === "number" && round > 1 && (
          <span className="wk-stages-round"> · round {round}</span>
        )}
      </div>
      <div className="wk-stages-row">
        {STAGES.map((s, i) => {
          // Stepper grammar (Glass & Print): stages behind the live one read
          // as done (ink), the live one is the only glass pill, the rest hollow.
          const activeIndex = active ? STAGES.indexOf(active) : -1;
          const isActive = active === s;
          const isDone = activeIndex >= 0 && i < activeIndex;
          return (
            <span
              className={"wk-stage-grp" + (isDone || isActive ? " is-done" : "")}
              key={s}
            >
              {i > 0 && <span className="wk-stage-line" aria-hidden />}
              <span
                className={
                  "wk-stage" + (isActive ? " is-active" : isDone ? " is-done" : "")
                }
              >
                {isActive ? (
                  <Loader2
                    className="wk-stage-spin wk-spin"
                    width={11}
                    height={11}
                    aria-hidden
                  />
                ) : (
                  <span className="wk-stage-dot" aria-hidden />
                )}
                <span className="wk-stage-lab">{s}</span>
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}
